/* eslint-disable */
/**
 * QuickEnrollByQuestionnaire
 * ==========================
 * Modal reutilisable pour enroler rapidement des patients dans une etude
 * EN FONCTION DU QUESTIONNAIRE SELECTIONNE.
 *
 * Flow :
 *   1. IDE/MEDECIN/ADMIN ouvre la modal
 *   2. Selectionne un questionnaire dans le dropdown (filtre par cancerType)
 *   3. La liste des patients matching la pathologie s'affiche
 *   4. Selectionne les patients + selectionne une etude active compatible
 *   5. Enrole d'un clic
 */
import { useState, useEffect, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  X, ClipboardList, Users, UserPlus, Search, CheckCircle,
  FlaskConical, Target, AlertCircle,
} from "lucide-react";

export default function QuickEnrollByQuestionnaire({ open, onClose, onEnrolled }) {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [studies, setStudies] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const [matchData, setMatchData] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  // Init
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [qRes, sRes] = await Promise.all([
          api.get("/studies/questionnaires"),
          api.get("/studies?status=active"),
        ]);
        setQuestionnaires(qRes.data || []);
        setStudies(sRes.data || []);
      } catch {
        toast.error("Erreur chargement");
      }
    })();
  }, [open]);

  // Refetch patients when questionnaire changes
  const fetchPatients = useCallback(async (code) => {
    if (!code) {
      setMatchData(null);
      return;
    }
    try {
      setLoadingMatch(true);
      const res = await api.get(`/studies/patients-for-questionnaire/${code}`);
      setMatchData(res.data);
      setSelectedPatients(new Set());
    } catch {
      toast.error("Erreur chargement patients");
      setMatchData(null);
    } finally {
      setLoadingMatch(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients(selectedCode);
  }, [selectedCode, fetchPatients]);

  // Filtered patients by local search
  const filteredPatients = (matchData?.patients || []).filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (p.firstName || "").toLowerCase().includes(s) ||
      (p.lastName || "").toLowerCase().includes(s) ||
      (p.cancerType || "").toLowerCase().includes(s)
    );
  });

  // Studies compatibles with this questionnaire
  const hasQuestionnaireInVisits = (study, code) => (
    Array.isArray(study.visitSchedule) && study.visitSchedule.some(v => (v.questionnaires || []).includes(code))
  );

  const hasQuestionnaireGloballyOnly = (study, code) => (
    Array.isArray(study.questionnaires) && study.questionnaires.includes(code) && !hasQuestionnaireInVisits(study, code)
  );

  const compatibleStudies = studies.filter(s =>
    hasQuestionnaireInVisits(s, selectedCode) || hasQuestionnaireGloballyOnly(s, selectedCode)
  );

  const togglePatient = (id) => {
    setSelectedPatients(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedPatients.size === filteredPatients.length) {
      setSelectedPatients(new Set());
    } else {
      setSelectedPatients(new Set(filteredPatients.map(p => p.id)));
    }
  };

  const handleEnroll = async () => {
    if (!selectedStudyId) {
      toast.error("Sélectionnez une étude");
      return;
    }
    if (selectedPatients.size === 0) {
      toast.error("Sélectionnez au moins un patient");
      return;
    }
    try {
      setEnrolling(true);
      const today = new Date().toISOString().split("T")[0];
      let ok = 0, fail = 0;
      for (const patientId of selectedPatients) {
        try {
          await api.post(`/studies/${selectedStudyId}/enroll`, {
            patientId,
            consentDate: today,
            consentMethod: "electronic",
          });
          ok++;
        } catch {
          fail++;
        }
      }
      toast.success(`${ok} patient(s) enrôlé(s)${fail ? `, ${fail} échec(s)` : ""}`);
      onEnrolled?.({ enrolled: ok, failed: fail });
      onClose();
    } finally {
      setEnrolling(false);
    }
  };

  if (!open) return null;

  const selectedQuestionnaire = questionnaires.find(q => q.code === selectedCode);
  const cancerTags = selectedQuestionnaire?.cancerTypes || [];
  const isGeneric = cancerTags.length === 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="quick-enroll-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">Enrôlement rapide par questionnaire</h3>
              <p className="text-xs text-slate-500">Sélectionnez un questionnaire → les patients de la pathologie cible s&rsquo;affichent</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-quick-enroll">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Step 1: select questionnaire */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
              1. Questionnaire
            </label>
            <select
              value={selectedCode}
              onChange={e => setSelectedCode(e.target.value)}
              data-testid="questionnaire-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">-- Sélectionner un questionnaire --</option>
              {questionnaires.map(q => (
                <option key={q.code} value={q.code}>
                  {q.name} {q.cancerTypes?.length ? `(${q.cancerTypes.join(", ")})` : "(Générique)"}
                </option>
              ))}
            </select>
            {selectedQuestionnaire && (
              <div className="flex items-center flex-wrap gap-2 pt-1">
                {isGeneric ? (
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <Target className="w-3 h-3" /> Générique — applicable à tous les patients
                  </span>
                ) : (
                  cancerTags.map(ct => (
                    <span key={ct} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
                      {ct}
                    </span>
                  ))
                )}
                <span className="text-xs text-slate-400">
                  {selectedQuestionnaire.itemCount} items · ~{selectedQuestionnaire.estimatedMinutes} min
                </span>
              </div>
            )}
          </div>

          {/* Step 2: patients matching */}
          {selectedCode && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center justify-between">
                <span>2. Patients correspondant à la pathologie</span>
                {matchData && (
                  <span className="font-normal normal-case text-slate-500" data-testid="matching-count">
                    {matchData.totalMatching} patient{matchData.totalMatching !== 1 ? "s" : ""}
                  </span>
                )}
              </label>

              {loadingMatch ? (
                <div className="text-center py-8 text-slate-400">Chargement...</div>
              ) : !matchData || matchData.totalMatching === 0 ? (
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Aucun patient ne correspond à cette pathologie</p>
                </div>
              ) : (
                <>
                  {/* Search + select-all */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Rechercher..."
                        data-testid="patient-search-input"
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={toggleAll}
                      data-testid="toggle-all-patients"
                      className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      {selectedPatients.size === filteredPatients.length && filteredPatients.length > 0
                        ? "Tout décocher" : "Tout cocher"}
                    </button>
                  </div>

                  {/* Patients list */}
                  <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="patients-list">
                    {filteredPatients.map(p => {
                      const checked = selectedPatients.has(p.id);
                      return (
                        <label
                          key={p.id}
                          className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                            checked ? "bg-indigo-50" : "hover:bg-slate-50"
                          }`}
                          data-testid={`patient-row-${p.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePatient(p.id)}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {p.firstName} {p.lastName}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {p.cancerType}
                              {p.cancerSubtype ? ` · ${p.cancerSubtype}` : ""}
                              {p.stage ? ` · ${p.stage}` : ""}
                              {p.protocol ? ` · ${p.protocol}` : ""}
                            </p>
                          </div>
                          {p.currentCycleNumber && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">
                              C{p.currentCycleNumber}
                            </span>
                          )}
                        </label>
                      );
                    })}
                    {filteredPatients.length === 0 && (
                      <div className="p-6 text-center text-sm text-slate-400">
                        Aucun patient ne correspond à la recherche
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: select study */}
          {selectedCode && matchData && matchData.totalMatching > 0 && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                3. Étude dans laquelle enrôler
              </label>
              {compatibleStudies.length === 0 ? (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700">
                    Aucune étude active n&rsquo;inclut ce questionnaire dans son calendrier. Créez-en une via
                    <span className="font-semibold"> Super Admin → DATA → Études</span>.
                  </div>
                </div>
              ) : (
                <>
                <select
                  value={selectedStudyId}
                  onChange={e => setSelectedStudyId(e.target.value)}
                  data-testid="study-select"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                >
                  <option value="">-- Sélectionner une étude --</option>
                  {compatibleStudies.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.sponsor}
                    </option>
                  ))}
                </select>
                {selectedStudyId && hasQuestionnaireGloballyOnly(studies.find(s => s.id === selectedStudyId) || {}, selectedCode) && (
                  <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="baseline-auto-attach-warning">
                    <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      Ce questionnaire est dans l’étude mais pas encore dans une visite. Hana l’assignera automatiquement à Baseline lors de l’enrôlement.
                    </p>
                  </div>
                )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-between bg-slate-50">
          <div className="text-sm text-slate-600">
            {selectedPatients.size > 0 && (
              <span className="flex items-center gap-1.5 font-medium text-indigo-700" data-testid="selected-count">
                <CheckCircle className="w-4 h-4" />
                {selectedPatients.size} patient{selectedPatients.size > 1 ? "s" : ""} sélectionné{selectedPatients.size > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
            >
              Annuler
            </button>
            <button
              onClick={handleEnroll}
              disabled={!selectedStudyId || selectedPatients.size === 0 || enrolling}
              data-testid="confirm-enroll-btn"
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              {enrolling ? "Enrôlement..." : `Enrôler ${selectedPatients.size || ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
